import React, { useEffect, useMemo, useState } from 'react';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import {
  createProposal, getCountries, getSpecialities, getStates, listOf,
} from './vendorsApi';
import {
  ISO_MISSING, NDA_WARNING, RISK_LABEL, DATA_ACCESS_LABEL, NDA_LABEL, CONTRACT_LABEL,
  VENDOR_ERRORS, atLeast,
} from './vendorLabels';
import {
  FORM_GROUPS, NOTE_MAX, buildChanges, initialValues, needsNdaWarning, sameIds, validate,
} from './vendorFormModel';
import { Drawer } from './VendorBits';

const OPTION_LABELS = { RISK: RISK_LABEL, DATA_ACCESS: DATA_ACCESS_LABEL, NDA: NDA_LABEL, CONTRACT: CONTRACT_LABEL };

// The specialities a person may propose into: propose or higher (spec 4.2).
// A vendor's current ones stay listed so an edit shows its real state.
function useSpecialityOptions(me, vendor) {
  const [all, setAll] = useState([]);
  useEffect(() => {
    if (!me?.is_admin) return undefined;
    let live = true;
    getSpecialities()
      .then((d) => { if (live) setAll(listOf(d, 'specialities')); })
      .catch(() => { /* fail open: admin falls back to their own levels */ });
    return () => { live = false; };
  }, [me]);
  return useMemo(() => {
    const byId = new Map();
    (me?.levels || []).filter((l) => atLeast(l.level, 'propose')).forEach((l) => byId.set(l.tag_id, { id: l.tag_id, name: l.name }));
    all.forEach((s) => byId.set(s.id, s));
    (vendor?.specialities || []).forEach((s) => { if (!byId.has(s.id)) byId.set(s.id, s); });
    return [...byId.values()];
  }, [me, all, vendor]);
}

function FieldError({ id, message }) {
  return message ? <p id={id} className="vd-field-error" role="alert">{message}</p> : null;
}

/**
 * Propose / add vendor drawer. Edit submits only what changed. Validation is
 * inline before submit (never a toast); the NDA rule warns but never blocks.
 * Mount it only while open: it seeds its state from `vendor` once.
 */
export default function VendorForm({
  open, mode, vendor, me, isoFieldsKnown = true, onClose, onSubmitted,
}) {
  const isEdit = mode === 'edit';
  const initial = useMemo(() => initialValues(vendor), [vendor]);
  const initialTagIds = useMemo(() => (vendor?.specialities || []).map((s) => s.id), [vendor]);
  const [values, setValues] = useState(initial);
  const [tagIds, setTagIds] = useState(isEdit ? initialTagIds : []);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countriesFailed, setCountriesFailed] = useState(false);
  const [states, setStates] = useState([]);
  const options = useSpecialityOptions(me, vendor);

  useEffect(() => {
    let live = true;
    getCountries()
      .then((d) => { if (live) setCountries(listOf(d, 'countries')); })
      .catch(() => { if (live) setCountriesFailed(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!values.country_id) { setStates([]); return undefined; }
    let live = true;
    getStates(Number(values.country_id))
      .then((d) => { if (live) setStates(listOf(d, 'states')); })
      .catch(() => { if (live) setStates([]); });
    return () => { live = false; };
  }, [values.country_id]);

  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));
  const toggleTag = (id) => setTagIds((cur) => (cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]));
  const warn = isoFieldsKnown && needsNdaWarning(values);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const tagsChanged = !sameIds(tagIds, initialTagIds);
    const found = validate(values, { tagIds, isoKnown: isoFieldsKnown });
    if (isEdit && !tagsChanged) delete found.tags;
    setErrors(found);
    setFormError('');
    if (Object.keys(found).length) return;

    const changes = buildChanges(initial, values, { mode, isoKnown: isoFieldsKnown });
    let body;
    if (!isEdit) {
      body = { kind: 'create', changes, tag_ids: tagIds };
    } else if (Object.keys(changes).length && tagsChanged) {
      setFormError('Change specialities in a separate proposal from the other fields.');
      return;
    } else if (Object.keys(changes).length) {
      body = { kind: 'edit', vendor_id: vendor.id, changes };
    } else if (tagsChanged) {
      body = { kind: 'retag', vendor_id: vendor.id, tag_ids: tagIds };
    } else {
      setFormError('Change at least one field before submitting.');
      return;
    }
    if (note.trim()) body.note = note.trim();

    setBusy(true);
    try {
      await createProposal(body);
      toastSuccess('Change proposed — the speciality owner will review it.');
      if (onSubmitted) onSubmitted();
    } catch (err) {
      const data = err?.response?.data;
      // The server named the field it rejected: show it next to that field.
      if ((data?.error === 'invalid_field' || data?.error === 'invalid_value') && data.field) {
        setErrors({ [data.field]: "This value isn't accepted. Check it and try again." });
      } else {
        toastError(errorMessage(err, 'Could not submit the proposal.', VENDOR_ERRORS));
      }
    } finally {
      setBusy(false);
    }
  };

  const control = (f, readOnly) => {
    const id = `vd-f-${f.key}`;
    const err = errors[f.key];
    const common = { id, disabled: busy || readOnly, 'aria-invalid': err ? 'true' : undefined, 'aria-describedby': err ? `${id}-err` : undefined };
    if (f.kind === 'bool') {
      return (
        <label className="vd-radio" htmlFor={id}>
          <input type="checkbox" {...common} checked={!!values[f.key]} onChange={(e) => set(f.key, e.target.checked)} />
          {f.label}
        </label>
      );
    }
    let input;
    if (f.kind === 'select') {
      const labels = OPTION_LABELS[f.options];
      input = (
        <select {...common} value={values[f.key]} onChange={(e) => set(f.key, e.target.value)}>
          <option value="">Not set</option>
          {Object.entries(labels).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      );
    } else if (f.kind === 'country') {
      input = (
        <select {...common} value={values.country_id} onChange={(e) => { set('country_id', e.target.value); set('state_id', ''); }}>
          <option value="">{isEdit && vendor?.country_name ? `${vendor.country_name} (current)` : 'Select a country'}</option>
          {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      );
    } else if (f.kind === 'state') {
      input = (
        <select {...common} value={values.state_id} onChange={(e) => set('state_id', e.target.value)} disabled={busy || states.length === 0}>
          <option value="">{isEdit && vendor?.state_name ? `${vendor.state_name} (current)` : 'Select a state'}</option>
          {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      );
    } else {
      const type = { email: 'email', url: 'text', date: 'date' }[f.kind] || 'text';
      input = <input type={type} {...common} maxLength={f.max} value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} />;
    }
    return (
      <>
        <label htmlFor={id}>{f.label}</label>
        {input}
        <FieldError id={`${id}-err`} message={err} />
      </>
    );
  };

  return (
    <Drawer open={open} title={isEdit ? 'Propose changes' : 'Add vendor'} onClose={onClose} wide>
      <form onSubmit={submit} noValidate>
        {formError && <p className="vd-inline-error" role="alert">{formError}</p>}

        <fieldset className="vd-group vd-fieldset">
          <legend><h3>Specialities</h3></legend>
          <div className="vd-filter-chips" role="group" aria-label="Specialities">
            {options.map((s) => (
              <button key={s.id} type="button" className="vd-toggle-chip" aria-pressed={tagIds.includes(s.id)} onClick={() => toggleTag(s.id)} disabled={busy}>
                {s.name}
              </button>
            ))}
          </div>
          {options.length === 0 && <p className="vd-muted">You can&apos;t propose into any speciality yet.</p>}
          <FieldError id="vd-tags-err" message={errors.tags} />
        </fieldset>

        {FORM_GROUPS.map((g) => (
          <section key={g.title} className="vd-group" aria-label={g.title}>
            <h3>{g.title}</h3>
            {g.iso && !isoFieldsKnown ? (
              <p className="vd-muted">{ISO_MISSING}</p>
            ) : (
              g.fields.map((f) => (
                <div key={f.key} className="vd-form-row">{control(f, false)}</div>
              ))
            )}
            {g.iso && warn && <p className="vd-warning" role="status">{NDA_WARNING}</p>}
            {g.title === 'Contact' && countriesFailed && <p className="vd-muted">Couldn&apos;t load countries.</p>}
          </section>
        ))}

        <div className="vd-form-row">
          <label htmlFor="vd-f-note">Why this change</label>
          <textarea id="vd-f-note" rows={3} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
          <span className="vd-counter">{note.length}/{NOTE_MAX}</span>
        </div>

        <div className="vd-drawer-foot">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>Submit proposal</button>
        </div>
      </form>
    </Drawer>
  );
}
